import {
  cloneElement,
  FC,
  HTMLAttributes,
  ReactElement,
  useMemo,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { Text } from 'components/core/text';
import { formatInTimeZone } from 'date-fns-tz';
import { debounce } from 'lodash';
import { Calendar, Clock } from 'react-feather';

import { Slot } from '../api/getAvailability';
import { AppointmentDetailBubbles } from '../Appointments/AppointmentDetailBubbles';
import { Appointment } from '../Appointments/types';
import { Avatar } from '../Avatar/Avatar';
import { ActionTile } from '../common/Tile';
import { BendBigCircle } from '../icons/BendBigCircle';
import { useCalendarContext } from './CalendarProvider';

interface Props {
  appointment: Appointment;
  slot: Slot;
  BackButton: ReactElement;
  ConfirmPreamble?: ReactElement;
  ConfirmButton: ReactElement;
  onReschedule?: (appt: Appointment) => void;
  onError: () => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
`;

const ApptNameText = styled(Text)`
  font-size: 24px;
  line-height: 34px;
  letter-spacing: 0em;
  font-weight: bold;

  @media (--mobile) {
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0em;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const SpacerArrow: FC<HTMLAttributes<SVGElement>> = props => (
  <svg
    width="16"
    height="52"
    viewBox="0 0 16 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M9 1C9 0.447715 8.55228 2.41411e-08 8 0C7.44771 3.54635e-08 7 0.447715 7 1L9 1ZM7.29289 51.7071C7.68342 52.0976 8.31658 52.0976 8.70711 51.7071L15.0711 45.3431C15.4616 44.9526 15.4616 44.3195 15.0711 43.9289C14.6805 43.5384 14.0474 43.5384 13.6569 43.9289L8 49.5858L2.34315 43.9289C1.95262 43.5384 1.31946 43.5384 0.928932 43.9289C0.538408 44.3195 0.538408 44.9526 0.928932 45.3431L7.29289 51.7071ZM7 1L7 51L9 51L9 1L7 1Z"
      fill="#292927"
    />
  </svg>
);

export const CalendarConfirmation: FC<Props> = ({
  appointment,
  slot,
  BackButton,
  ConfirmPreamble,
  ConfirmButton,
  onError,
  onReschedule,
}) => {
  const context = useCalendarContext();
  const [isRequesting, setIsRequesting] = useState(false);
  const CLICK_DEBOUNCE_TIME_MS = 500;

  const debouncedOnClick = useMemo(
    () =>
      debounce(async (e: MouseEvent) => {
        setIsRequesting(true);
        try {
          if (appointment.scheduledStartTime) {
            const newAppt = await context.rescheduleAppointment(slot);
            onReschedule?.(newAppt);
          } else {
            await context.confirmAppointment(slot);
          }
          ConfirmButton.props?.onClick(e);
        } catch (e) {
          onError();
        } finally {
          setIsRequesting(false);
        }
      }, CLICK_DEBOUNCE_TIME_MS),
    [
      ConfirmButton,
      appointment.scheduledStartTime,
      context,
      slot,
      onReschedule,
      onError,
    ],
  );

  const ActualConfirmButton = useMemo(
    () =>
      cloneElement(ConfirmButton, {
        disabled: isRequesting || ConfirmButton.props.disabled,
        onClick: (e: MouseEvent) => {
          setIsRequesting(true);
          debouncedOnClick(e);
          setIsRequesting(false);
        },
        isLoading: context.isConfirming,
      }),
    [ConfirmButton, isRequesting, context.isConfirming, debouncedOnClick],
  );

  const date = useMemo(
    () =>
      formatInTimeZone(
        new Date(slot.startTime),
        context.selectedTimeZone,
        'EEEE, LLLL d, yyyy',
      ),
    [context.selectedTimeZone, slot.startTime],
  );
  const time = useMemo(
    () =>
      formatInTimeZone(
        new Date(slot.startTime),
        context.selectedTimeZone,
        'h:mm aa',
      ),
    [context.selectedTimeZone, slot.startTime],
  );

  const originalDate = useMemo(() => {
    if (!appointment.scheduledStartTime) return null;
    return formatInTimeZone(
      new Date(appointment.scheduledStartTime),
      appointment.timeZone ?? 'America/Chicago',
      'EEEE, LLLL d, yyyy',
    );
  }, [appointment.scheduledStartTime, appointment.timeZone]);

  const originalTime = useMemo(() => {
    if (!appointment.scheduledStartTime) return null;
    return formatInTimeZone(
      new Date(appointment.scheduledStartTime),
      appointment.timeZone ?? 'America/Chicago',
      'h:mm aa',
    );
  }, [appointment.scheduledStartTime, appointment.timeZone]);

  return (
    <Container>
      {BackButton}
      <Text variant="text2" className="pb-4">
        Review and Confirm:
      </Text>
      <ActionTile
        className="w-full"
        actionButton={
          <>
            {ConfirmPreamble}
            {ActualConfirmButton}
          </>
        }
      >
        <div className="flex">
          <div className="flex flex-col items-center">
            {appointment.providerAccountId ||
            slot.avatarUrlDefault ||
            slot.credentialedName ? (
              <Avatar
                appointmentType={appointment.appointmentType}
                url={appointment.avatarUrlDefault || slot.avatarUrlDefault}
                name={
                  (appointment.credentialedName || slot.credentialedName) ??
                  'N/A'
                }
                style={{ marginRight: 0, alignSelf: 'flex-start' }}
              />
            ) : (
              <BendBigCircle style={{ width: 64, height: 64 }} />
            )}
            {originalDate && (
              <>
                <Text style={{ marginTop: 'auto' }}>From</Text>
                <SpacerArrow style={{ margin: '6px 0' }} />
                <Text style={{ marginBottom: 9 }}>To</Text>
              </>
            )}
          </div>
          <div className="flex flex-col pl-4 items-start">
            <ApptNameText>{appointment.name}</ApptNameText>
            <Text block bold variant="text4">
              {appointment.credentialedName || slot.credentialedName}
            </Text>
            <AppointmentDetailBubbles appointment={appointment} />

            {originalDate && (
              <>
                <Row className="pt-6">
                  <Calendar />
                  <Text>&nbsp;{originalDate}</Text>
                </Row>
                <Row>
                  <Clock />
                  <Text>&nbsp;{originalTime}</Text>
                </Row>
                <Row>{appointment.timeZone}</Row>
              </>
            )}

            <Row className="pt-6">
              <Calendar />
              <Text>&nbsp;{date}</Text>
            </Row>
            <Row>
              <Clock />
              <Text>&nbsp;{time}</Text>
            </Row>
            <Row>{context.selectedTimeZone}</Row>
          </div>
        </div>
      </ActionTile>
    </Container>
  );
};
